import { Button, Text } from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import React from "react";
import {
  DialogActionTrigger,
  DialogBackdrop,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
} from "../ui/dialog";

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  /**
   * Label for the confirm button. Defaults to `app.common.confirm`.
   */
  confirmLabel?: string;
  /**
   * Label for the cancel button. Defaults to `app.common.cancel`.
   */
  cancelLabel?: string;
  /**
   * Chakra colorPalette for the confirm button. Defaults to `blue`.
   * Use `red` for destructive actions (or prefer the dedicated `DeleteConfirmDialog`).
   */
  colorPalette?: "blue" | "red" | "purple" | "green" | "orange" | "teal";
  loading?: boolean;
}

/**
 * General-purpose confirmation dialog. Use this instead of `window.confirm` for any
 * user-facing confirmation flow. For destructive (delete) actions, prefer the
 * dedicated `DeleteConfirmDialog` which is preset to the red palette + Delete label.
 */
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  cancelLabel,
  colorPalette = "blue",
  loading = false,
}) => {
  const { t } = useTranslation();

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={(e: { open: boolean }) => (e.open ? undefined : onClose())}
    >
      <DialogPortal>
        <DialogBackdrop />
        <DialogPositioner>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <Text whiteSpace="pre-wrap">{message}</Text>
            </DialogBody>
            <DialogFooter>
              <DialogActionTrigger asChild>
                <Button variant="outline" onClick={onClose} disabled={loading}>
                  {cancelLabel ?? t("app.common.cancel")}
                </Button>
              </DialogActionTrigger>
              <Button
                colorPalette={colorPalette}
                onClick={onConfirm}
                loading={loading}
              >
                {confirmLabel ?? t("app.common.confirm")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </DialogPositioner>
      </DialogPortal>
    </DialogRoot>
  );
};

export default ConfirmDialog;
